<script setup>
import { router } from '@inertiajs/vue3'
import LoadingButton from '@/Shared/LoadingButton.vue'
import { computed } from 'vue'
import { useDate } from '@/Composables/useDate'
import Icon from '@/Shared/Icon.vue'

const props = defineProps({
  record: {
    type: Object,
    default: null
  },
  student: {
    type: Object,
    default: null
  }
})

function confirm() {
  router.post(`/${props.record.uuid}/student/${props.student.uuid}/accept`)
}

function reject() {
  router.get(`/${props.record.uuid}/student/${props.student.uuid}/reject`)
}

const alreadyJoined = computed(() => {
  return props.record.students.map((s) => s.id).includes(props.student.id)
})

const lessonExpired = computed(() => {
  return props.record.students.length >= props.record.lesson.max_students
})
</script>

<template>
  <div class="w-full max-w-xl mx-auto py-8 px-4">
    <!-- Tarjeta de información de la clase -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      <!-- Cabecera de la tarjeta -->
      <div
        class="p-5 border-b border-gray-100 flex justify-between items-center"
        :style="`background-color: ${
          record.lesson.level?.color || '#F3F4F6'
        }; background-opacity: 0.1;`"
      >
        <div class="flex items-center space-x-3">
          <div
            class="rounded-full h-10 w-10 flex items-center justify-center font-bold text-white"
            :style="`background-color: ${record.lesson.level?.color || '#4F46E5'};`"
          >
            {{ record.lesson.level?.name.charAt(0) || 'C' }}
          </div>
          <div>
            <h3 class="font-semibold text-gray-900">{{ record.lesson.level?.name || 'Clase' }}</h3>
            <div class="text-xs text-gray-500">
              {{ useDate(record.date_start).format('D [de] MMMM [de] YYYY') }}
            </div>
          </div>
        </div>

        <div class="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
          {{ record.students.length }} / {{ record.lesson.max_students }} alumnos
        </div>
      </div>

      <!-- Contenido de la tarjeta -->
      <div class="p-5 space-y-4">
        <!-- Horario -->
        <div class="flex items-center space-x-3">
          <div class="bg-indigo-100 text-indigo-600 p-2 rounded-lg">
            <Icon name="Clock" class="h-5 w-5" />
          </div>
          <div>
            <div class="text-sm font-medium text-gray-900">
              {{ useDate(record.date_start).format('HH:mm') }} -
              {{ useDate(record.time_end).format('HH:mm') }}
            </div>
            <div class="text-xs text-gray-500">{{ useDate(record.date_start).format('dddd') }}</div>
          </div>
        </div>

        <!-- Entrenador -->
        <div class="flex items-center space-x-3">
          <div class="bg-green-100 text-green-600 p-2 rounded-lg">
            <Icon name="User" class="h-5 w-5" />
          </div>
          <div>
            <div class="text-sm font-medium text-gray-900">
              {{ record.lesson.coach?.player?.fullname || 'Entrenador' }}
            </div>
            <div class="text-xs text-gray-500">Entrenador</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estado de la confirmación -->
    <div
      v-if="alreadyJoined"
      class="bg-white rounded-xl shadow-sm border border-green-200 overflow-hidden mb-6"
    >
      <div class="p-5 flex items-center space-x-4">
        <div class="bg-green-100 p-3 rounded-full">
          <Icon name="Check" class="h-6 w-6 text-green-600" />
        </div>
        <div>
          <h3 class="font-medium text-gray-900">¡Ya estás inscrito!</h3>
          <p class="text-sm text-gray-500 mt-1">
            Ya te has unido a la clase del
            {{ useDate(record.date_start).format('D [de] MMMM [de] YYYY [a las] HH:mm') }}.
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="!alreadyJoined && lessonExpired"
      class="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden mb-6"
    >
      <div class="p-5 flex items-center space-x-4">
        <div class="bg-red-100 p-3 rounded-full">
          <Icon name="X" class="h-6 w-6 text-red-600" />
        </div>
        <div>
          <h3 class="font-medium text-gray-900">Clase completa</h3>
          <p class="text-sm text-gray-500 mt-1">
            Lo sentimos, ya no es posible unirte a esta clase porque ha alcanzado el máximo de
            alumnos.
          </p>
        </div>
      </div>
    </div>

    <!-- Formulario de confirmación -->
    <div
      v-if="!lessonExpired && !alreadyJoined"
      class="bg-white rounded-xl shadow-sm border border-indigo-200 overflow-hidden mb-6"
    >
      <div class="p-5">
        <div class="flex items-center space-x-4 mb-4">
          <div class="bg-indigo-100 p-3 rounded-full">
            <Icon name="Information" class="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h3 class="font-medium text-gray-900">Confirma tu asistencia</h3>
            <p class="text-sm text-gray-500 mt-1">
              Hola {{ student.player.fullname }}, ¿quieres unirte a esta clase?
            </p>
          </div>
        </div>

        <div class="flex space-x-4 mt-6">
          <LoadingButton
            type="button"
            class="flex-1 btn-indigo py-2 flex items-center justify-center"
            @click="confirm"
          >
            <Icon name="Check" class="h-5 w-5 mr-2" />
            Confirmar
          </LoadingButton>
          <LoadingButton
            type="button"
            class="flex-1 btn-white py-2 flex items-center justify-center"
            @click="reject"
          >
            <Icon name="X" class="h-5 w-5 mr-2" />
            Rechazar
          </LoadingButton>
        </div>
      </div>
    </div>

    <!-- Lista de alumnos -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div class="p-5 border-b border-gray-100">
        <h3 class="font-semibold text-gray-900 flex items-center">
          <Icon name="Users" class="h-5 w-5 mr-2 text-indigo-500" />
          Alumnos que acudirán
        </h3>
      </div>

      <div class="p-5">
        <div v-if="record.students.length > 0" class="space-y-3">
          <div
            v-for="(student, index) in record.students"
            :key="index"
            class="flex items-center space-x-3"
          >
            <div
              class="bg-indigo-100 text-indigo-700 rounded-full h-8 w-8 flex items-center justify-center font-medium"
            >
              {{ student.player.name.charAt(0) }}{{ student.player.lastname.charAt(0) }}
            </div>
            <div class="text-sm text-gray-700">{{ student.player.fullname }}</div>
          </div>
        </div>
        <div v-else class="text-center py-4">
          <div class="bg-gray-50 p-4 rounded-full inline-block mb-3">
            <Icon name="Users" class="h-6 w-6 text-gray-400" />
          </div>
          <p class="text-gray-500">No hay alumnos todavía</p>
        </div>
      </div>
    </div>
  </div>
</template>
